import React from 'react';
import { Calculator, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров расчета НДС
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  vatRates,
  customVatRate,
  setCustomVatRate
}) => {
  // Расчет текущей суммы
  const amount = parseFloat(formData.amount) || 0;
  
  // Обработчик изменения суммы
  const handleAmountChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      amount: newValue
    }));
  };

  // Обработчик изменения пользовательской ставки НДС
  const handleCustomRateChange = (e) => {
    const newValue = e.target.value;
    setCustomVatRate(newValue);
  };
  
  // Предопределенные суммы для быстрого выбора
  const predefinedAmounts = [
    { value: '1000', label: '1 тыс.' },
    { value: '10000', label: '10 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '1000000', label: '1 млн' }
  ];

  // Функция расчета
  const calculateVAT = () => {
    // Эмулируем нажатие кнопки расчета в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-green-500" />
        Быстрый расчет НДС
      </h3>
      
      <div className="space-y-5">
        {/* Сумма */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="amountSlider" className="text-gray-600 dark:text-gray-400">Сумма</label>
            <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(amount, 0)}</span>
          </div>
          <input
            type="range"
            id="amountSlider"
            min="0"
            max="1000000"
            step="1000"
            value={amount}
            onChange={handleAmountChange}
            aria-label="Сумма"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAmounts.map(amount => (
              <button
                key={amount.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, amount: amount.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.amount === amount.value 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {amount.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Тип расчета */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Тип расчета
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'add' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.calculationType === 'add' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Начислить НДС</div>
              <div className="text-xs opacity-75">К исходной сумме</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, calculationType: 'extract' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.calculationType === 'extract' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Выделить НДС</div>
              <div className="text-xs opacity-75">Из общей суммы</div>
            </button>
          </div>
        </div>
        
        {/* Ставка НДС */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Ставка НДС
          </div>
          <div className="grid grid-cols-3 gap-1 mb-2">
            {['20', '10', '0'].map(rate => (
              <button
                key={rate}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, vatRate: rate }))}
                className={`px-2 py-2 text-sm rounded border transition-colors
                  ${formData.vatRate === rate 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate}%
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-1">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, vatRate: 'none' }))}
              className={`px-2 py-2 text-sm rounded border transition-colors
                ${formData.vatRate === 'none' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Без НДС
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, vatRate: 'custom' }))}
              className={`px-2 py-2 text-sm rounded border transition-colors
                ${formData.vatRate === 'custom' 
                  ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              Своя ставка
            </button>
          </div>
        </div>
        
        {/* Пользовательская ставка НДС (если выбрана) */}
        {formData.vatRate === 'custom' && (
          <div>
            <div className="flex justify-between items-center mb-1 text-sm">
              <label htmlFor="customVatRateInput" className="text-gray-600 dark:text-gray-400">Своя ставка НДС (%)</label>
              <span className="font-medium text-gray-900 dark:text-white">{customVatRate}%</span>
            </div>
            <input
              type="range"
              id="customVatRateInput"
              min="0"
              max="30"
              step="0.5"
              value={customVatRate}
              onChange={handleCustomRateChange}
              aria-label="Своя ставка НДС"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-green-600"
            />
            <div className="grid grid-cols-4 gap-1 mt-2">
              {['5', '13', '18', '25'].map(rate => (
                <button
                  key={rate}
                  type="button"
                  onClick={() => setCustomVatRate(rate)}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${customVatRate === rate 
                      ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  {rate}%
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Точность расчетов */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Точность (знаков после запятой)
          </div>
          <div className="grid grid-cols-5 gap-1">
            {['0', '1', '2', '3', '4'].map(precision => (
              <button
                key={precision}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, precision: precision }))}
                className={`px-2 py-2 text-sm rounded border transition-colors
                  ${formData.precision === precision 
                    ? 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {precision}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateVAT}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать НДС
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;